import { useNCTextLocalizer } from '../../../locale/useNCTextLocalizer';
import { TablePosition, CellDescriber, TableColumnProps } from '../shared';
import { TableRegions } from '../TableRegions';
import { BaseTableProps, Defaults, hasSelectionColumn } from './internal';
import { ReactNode, ReactElement } from 'react';
import { Localizer } from '../../../locale/useTextLocalizer';
import { isNullish } from '@sas/nova-core/typeguards';
import { useIconA11yInfo } from '../../Icon/useIconA11yInfo';

export type { CellDescriber };

export interface TableNarration {
  createTableNarration: () => string | null;
  createNarration: (currPos: TablePosition, prevPos?: TablePosition) => string | null;
  getColumnWidthStartNarration: () => string;
  getColumnWidthChangeNarration: (width: string) => string;
}

export interface TableNarrationProps {
  columns: readonly Readonly<TableColumnProps>[];
  selectionMode: BaseTableProps['selectionMode'];
  selectedRows: BaseTableProps['selectedRows'];
  selectAll: BaseTableProps['selectAll'];
  renderCell: BaseTableProps['renderCell'];
  getRowKey: BaseTableProps['getRowKey'];
  columnHeadersVisible: BaseTableProps['columnHeadersVisible'];
  frozenColumnCount: BaseTableProps['frozenColumnCount'];
  rowCount: BaseTableProps['rowCount'];
  infoProvider?: CellDescriber;
  localizer?: Localizer;
  actionNarration?: string;
  disabled?: boolean;
  readOnly?: boolean;
  columnSpan?: (rowIndex: number, colIndex: number) => number;
  isRowDisabled?: BaseTableProps['isRowDisabled'];
  isRowReadOnly?: BaseTableProps['isRowReadOnly'];
  onSayCell?: (params: {
    current: TablePosition;
    previous: TablePosition | undefined;
    default: string | null;
  }) => string | null; // createCellNarration(currPos);
  onSaySelectionCell?: (params: {
    current: TablePosition;
    previous: TablePosition | undefined;
    default: string | null;
  }) => string | null;
  disableRowChangeNarration?: boolean;
  getSelectionCheckboxState?: BaseTableProps['getSelectionCheckboxState'];
  getSelectAllCheckboxState?: BaseTableProps['getSelectAllCheckboxState'];
  getSelectionState?: BaseTableProps['getSelectionState'];
  ariaRoleDescription?: BaseTableProps['ariaRoleDescription'];
  singleSelectionOverrides?: BaseTableProps['singleSelectionOverrides'];
  hideSelectionControlColumn?: BaseTableProps['hideSelectionControlColumn'];
}

export const useTableNarration = (props: TableNarrationProps): TableNarration => {
  const {
    selectionMode,
    selectedRows,
    selectAll,
    columnHeadersVisible,
    frozenColumnCount,
    rowCount,
    infoProvider,
    disabled,
    readOnly,
    columnSpan,
    isRowDisabled,
    isRowReadOnly,
    getSelectionCheckboxState,
    getSelectAllCheckboxState,
    getSelectionState,
    singleSelectionOverrides,
    hideSelectionControlColumn,
  } = props;
  const columns = props.columns ?? [];
  const localize = props.localizer ?? useNCTextLocalizer('Table');
  const hasRowSelectionHeader = hasSelectionColumn(
    selectionMode,
    columns.length,
    rowCount,
    hideSelectionControlColumn
  );
  const frozenOffset = hasRowSelectionHeader ? 1 : 0; // because row selection header is internally a frozen column
  const getRowKey = props.getRowKey ?? Defaults.getRowKey;
  const renderCell = props.renderCell ?? Defaults.renderCell;
  const iconInfo = useIconA11yInfo();
  const allowDeselection = singleSelectionOverrides?.allowDeselection;

  const getColumnHeaderContent = (pos: TablePosition): ReactNode => {
    const column = columns[pos.colIndex];
    if (column) {
      if (typeof infoProvider?.getColumnInfo === 'function') {
        const description = infoProvider.getColumnInfo({
          columnIndex: pos.colIndex,
          column,
        });
        if (description !== null) {
          return description;
        }
      }
      if (!column.label && column.icon) {
        return iconInfo({ icon: column.icon }).ariaLabel;
      }
      return column.label;
    }

    return null;
  };

  const getTableCellContent = (pos: TablePosition): ReactNode => {
    const column = columns[pos.colIndex];
    try {
      if (pos.rowIndex > -1 && column) {
        if (typeof column.cellContent === 'function') {
          const content = column.cellContent(pos.colIndex, pos.rowIndex, column.key);
          return content;
        }

        const content = renderCell({
          columnIndex: pos.colIndex,
          rowIndex: pos.rowIndex,
          columnKey: column.key,
        });
        return content;
      }
    } catch (err) {
      return null;
    }

    return null;
  };

  const getAccInfoOfCell = (position: TablePosition): string | null => {
    const column = columns[position.colIndex];
    if (column && typeof infoProvider?.getCellInfo === 'function') {
      const accInfo = infoProvider.getCellInfo({
        columnIndex: position.colIndex,
        rowIndex: position.rowIndex,
        column,
      });
      if (!isNullish(accInfo)) {
        return accInfo;
      }
    }

    const control = getTableCellContent(position);
    if (!control) return null;
    const asElement = control as ReactElement;
    let description = null;

    if (typeof control === 'string') {
      description = control as string;
    } else if (asElement.props['aria-label']) {
      description = asElement.props['aria-label'];
    } else if (asElement && typeof asElement.props.children === 'string') {
      description = asElement.props.children;
    }
    return description;
  };

  // is the given row at rowIndex selected?
  const isRowSelected = (rowIndex: number): boolean | 'mixed' => {
    const rowKey = getRowKey(rowIndex);
    return (
      getSelectionCheckboxState?.(rowKey) ??
      getSelectionState?.(rowKey) ??
      (selectAll === true || !!selectedRows?.has(rowKey))
    );
  };

  // is the given row at rowIndex disabled?
  const isDisabled = (rowIndex: number): boolean =>
    !!disabled || !!(rowIndex > -1 && !!isRowDisabled?.(getRowKey(rowIndex)));

  // is the given row at rowIndex read-only?
  const isReadOnly = (rowIndex: number): boolean =>
    !!readOnly || !!(rowIndex > -1 && !!isRowReadOnly?.(getRowKey(rowIndex)));

  /**
   * Generate and return text describing the selection state of the given cell, row, or column.
   * @param {TablePosition} currPos the table position to describe
   * @returns {string|null} the message or null if no message is needed
   */
  const createSelectionStateNarration = (currPos: TablePosition): string | null =>
    selectionMode !== 'none' && currPos.rowIndex > -1 && isRowSelected(currPos.rowIndex) === true
      ? localize('rowSelected.msg')
      : isRowSelected(currPos.rowIndex) === 'mixed'
      ? localize('partiallySelected.msg')
      : null;

  /**
   * Generate and return text describing the label (or labels) for the given column.
   * @param {TablePosition} currPos the table position to describe
   * @returns {string|null} the message or null if no message is needed
   */
  const createColumnLabelNarration = (currPos: TablePosition): string | null => {
    // no label if not showing column header
    if (columnHeadersVisible !== false && currPos.colIndex > (hasRowSelectionHeader ? 0 : -1)) {
      const description = getColumnHeaderContent(currPos);
      if (typeof description === 'string') {
        return `${description},`;
      }
    }

    return null;
  };

  /**
   * Generate and return text describing the row header created by cell values of frozen columns.
   * @param {TablePosition} currPos the table position to describe
   * @returns {string|null} the message or null if no message is needed
   */
  const createFrozenColumnRowHeaderNarration = (currPos: TablePosition): string | null => {
    const count = frozenColumnCount ?? 0;
    if (count > 0 && currPos.rowIndex > -1) {
      const frozenStart = hasRowSelectionHeader ? 1 : 0;
      const frozenEnd = frozenStart + count;
      const headers: string[] = [];

      for (let i = frozenStart; i < frozenEnd; i++) {
        const value = createCellNarration({
          ...currPos,
          colIndex: i,
        });
        if (value !== null) {
          headers.push(value);
        }
      }

      return headers.join(', ');
    }

    return null;
  };

  /**
   * Generate and return text describing the given cell.
   * @param {TablePosition} currPos the cell to describe
   * @returns {string|null} the message or null if no message is needed
   */
  const createCellNarration = (currPos: TablePosition): string | null => {
    const description = getAccInfoOfCell(currPos);

    // return blank cell description as a default
    return description ?? localize('blankCell.msg');
  };

  /**
   * Generate and return the narration message for the status of the given cell within the table.  Status elements
   * include whether the row and/or column is frozen and if the column is filtered or sorted.
   * @param {TablePosition} pos the cell being described
   * @returns {string|null} the message to pass to the narrator describing the status of the cell
   */
  const createCellStatusNarration = (pos: TablePosition) => {
    const column = columns[pos.colIndex];
    const isFrozen = pos.colIndex < (frozenColumnCount ?? 0) + frozenOffset;
    const isFiltered = false; // TODO
    const aMessageParts = [];
    const totalSortedColumns = columns.reduce((acc, currentColumn) => {
      return currentColumn.sortLevel && currentColumn.sortLevel > acc
        ? currentColumn.sortLevel
        : acc;
    }, 0);

    if (isNullish(column)) {
      return null;
    }

    if (isFrozen) {
      aMessageParts.push(localize('frozenColumn.msg'));
    }

    if (column.sortDirection === 'ascending') {
      aMessageParts.push(localize('sortedColumnAsc.msg'));
    } else if (column.sortDirection === 'descending') {
      aMessageParts.push(localize('sortedColumnDesc.msg'));
    }

    if (column.sortLevel && column.sortLevel > 0 && totalSortedColumns > 1) {
      aMessageParts.push(
        localize('sortedColumnLevel.msg', {
          sortLevel: column.sortLevel.toString(),
          totalSortedColumns: totalSortedColumns.toString(),
        })
      );
    }

    if (isFiltered) {
      aMessageParts.push(localize('filteredColumn.msg'));
    }

    if (aMessageParts.length > 0) {
      return `${aMessageParts.join(', ')},`;
    }

    return null;
  };

  /**
   * Generate and return text describing the position of the row within the table.
   * @param {TablePosition} pos the row to describe
   * @returns {string|null} the message or null if no message is needed
   */
  const createRowPositionNarration = (pos: TablePosition, useFull?: boolean) => {
    if (pos.rowIndex < 0) {
      return localize('inHeaderRow.msg');
    }

    if (pos.rowIndex >= rowCount) {
      return localize('inEmptyRow.msg');
    }

    const alt = props.ariaRoleDescription?.toLowerCase() === 'listbox' ? 'listbox.' : '';
    const message =
      useFull === true && !isNullish(rowCount) ? `${alt}rowPositionFull.msg` : 'rowPosition.msg';
    return localize(message, {
      current: (pos.rowIndex + 1).toString(), // top row of data should read as row 1
      total: rowCount?.toString(),
    });
  };

  /**
   * Generate and return text describing the position of the column within the table.
   * @param {TablePosition} pos the table position (column) to describe
   * @returns {string|null} the message or null if no message is needed
   */
  const createColumnPositionNarration = (pos: TablePosition, useFull?: boolean) => {
    const message = useFull === true ? 'columnPositionFull.msg' : 'columnPosition.msg';
    const total = columns.length;

    // the column index and total column count are based on the set of visible columns (disregarding any hidden ones)
    return localize(message, {
      current: (pos.colIndex + 1).toString(),
      total: total.toString(), // TODO all columns vs visible column (announce visible columns here)
    });
  };

  const createColumnSpanNarration = (
    currPos: TablePosition,
    columnSpan?: (rowIndex: number, columnIndex: number) => number
  ): string | null => {
    if (!columnSpan) return null;
    const colSpan = columnSpan(currPos.rowIndex, currPos.colIndex);
    if (colSpan <= 1) return null;
    return localize('columnSpan.msg', { columnSpan: colSpan.toString() });
  };

  const createPositionChangeNarration = (
    currPos: TablePosition,
    prevPos?: TablePosition
  ): string | null => {
    const aMessageParts = [];
    const colChanged: boolean = currPos.colIndex !== prevPos?.colIndex;
    const rowChanged: boolean = currPos.rowIndex !== prevPos?.rowIndex;

    // Column X
    if (colChanged && props.ariaRoleDescription !== 'listbox') {
      aMessageParts.push(createColumnPositionNarration(currPos, isNullish(prevPos)));
    }
    if (rowChanged && !props.disableRowChangeNarration) {
      aMessageParts.push(createRowPositionNarration(currPos, true));
    }

    return aMessageParts.length > 0 ? `${aMessageParts.join(', ')},` : null;
  };

  const handleZeroStateMessage = (_currPos: TablePosition, _prevPos?: TablePosition): string => {
    if (!columns || columns.length === 0) return `${localize('allColumnsHidden.msg')}`;
    else return `${localize('noItems.msg')}`;
  };

  const handleColumnHeaderButtonMessage = (
    _currPos: TablePosition,
    _prevPos?: TablePosition,
    disabled?: boolean
  ): string => {
    const disabledMsg = disabled ? localize('disabled.msg') : '';
    const buttonMsg = localize('columnHeaderButtonPosition.msg');
    return disabled ? `${buttonMsg}, ${disabledMsg}.` : `${buttonMsg}.`;
  };

  const handleSelectionColumnMessage = (
    currPos: TablePosition,
    prevPos?: TablePosition
  ): string => {
    const aMessageParts = [];
    const rowChanged: boolean = currPos.rowIndex !== prevPos?.rowIndex;
    const disabledRow: boolean = isDisabled(currPos.rowIndex);
    const readOnlyRow: boolean = isReadOnly(currPos.rowIndex);

    if (props.onSaySelectionCell) {
      // Prevent createCellNarration from being called on the Select All Checkbox
      const cellText = currPos.rowIndex < 0 ? '' : createCellNarration(currPos);
      const sText = props.onSaySelectionCell({
        current: currPos,
        previous: prevPos,
        default: cellText,
      });

      if (sText) {
        aMessageParts.push(`${sText},`);
      }
    }

    // Column span
    const columnSpanText = createColumnSpanNarration(currPos, columnSpan);
    if (columnSpanText !== null) {
      aMessageParts.push(columnSpanText);
    }

    // either a row selection header or select-all
    if (currPos.rowIndex < 0) {
      // in the select-all cell
      if (typeof infoProvider?.getSelectAllInfo === 'function') {
        const description = infoProvider.getSelectAllInfo();
        if (!isNullish(description)) {
          aMessageParts.push(description);
        }
      }

      const checkboxState = getSelectAllCheckboxState?.();
      aMessageParts.push(localize('inSelectAll.msg'));
      if (!disabledRow && !readOnly) {
        if (checkboxState !== undefined ? checkboxState === 'mixed' : selectAll === 'mixed') {
          aMessageParts.push(localize('mixedState.spacebarToSelect.msg'));
        } else if (checkboxState !== undefined ? checkboxState === true : selectAll === true) {
          aMessageParts.push(localize('spacebarToDeselect.msg'));
        } else {
          aMessageParts.push(localize('spacebarToSelect.msg'));
        }
      } else if (readOnly && selectAll) {
        if (checkboxState !== undefined ? checkboxState === 'mixed' : selectAll === 'mixed') {
          aMessageParts.push(localize('partiallyChecked.msg'));
        } else {
          aMessageParts.push(localize('selected.msg'));
        }
      }
      if (rowChanged) {
        aMessageParts.push(localize('inHeaderRow.msg'));
      }
    } else {
      // in a row selection header
      if (typeof infoProvider?.getRowSelectionHeaderInfo === 'function') {
        const description = infoProvider.getRowSelectionHeaderInfo({
          rowIndex: currPos.rowIndex,
          rowKey: getRowKey(currPos.rowIndex),
        });
        if (!isNullish(description)) {
          aMessageParts.push(description);
        }
      }

      const rowKey = getRowKey(currPos.rowIndex);
      const checkboxState = getSelectionCheckboxState?.(rowKey) ?? getSelectionState?.(rowKey);

      if (!props.disableRowChangeNarration) {
        aMessageParts.push(`${createRowPositionNarration(currPos, true)},`);
      }

      const rowSelected = isRowSelected(currPos.rowIndex);
      if (disabledRow) {
        aMessageParts.push(
          rowSelected
            ? checkboxState === 'mixed'
              ? localize('selectionHeader.partiallyChecked.disabled.msg')
              : localize('selectionHeader.disabled.msg')
            : localize('disabled.msg')
        );
      } else if (!readOnlyRow) {
        if (checkboxState === 'mixed') {
          aMessageParts.push(localize('mixedState.spacebarToSelect.msg'));
        } else if (rowSelected || checkboxState === true) {
          if (
            selectionMode !== 'singleRadio' ||
            (selectionMode === 'singleRadio' && !!allowDeselection)
          )
            aMessageParts.push(localize('spacebarToDeselect.msg'));
          else aMessageParts.push(localize('selected.msg'));
        } else {
          aMessageParts.push(localize('spacebarToSelect.msg'));
        }
      } else {
        if (rowSelected) {
          checkboxState === 'mixed'
            ? aMessageParts.push(localize('partiallySelected.msg'))
            : aMessageParts.push(localize('selected.msg'));
        }
      }
    }

    const colChanged: boolean = currPos.colIndex !== prevPos?.colIndex;

    // Column X
    if (colChanged) {
      aMessageParts.push(createColumnPositionNarration(currPos, isNullish(prevPos)));
    }

    // join everything together with spaces (an empty array returns an empty string)
    return aMessageParts.join(' ');
  };

  /**
   * Generate and return text describing the table.
   * @returns {string|null} the message or null if no message is needed
   */
  const createTableNarratorMessage = (): string | null => {
    const aMessageParts = [];
    let sMessage = null;

    if (readOnly) {
      aMessageParts.push(`${localize('readOnly.msg')}`);
    }

    // build a complete message if any of the above resulted in narration text
    if (aMessageParts.length > 0) {
      // join everything together with spaces (an empty array returns an empty string)
      sMessage = aMessageParts.join(' ');
    }

    return sMessage;
  };

  /**
   * Generate and return text describing the cell, row, and/or column based on the given parameters.
   * 1. 'Row selected', if entering a new row and row has been selected
   * 2. Context changes column/row headers as appropriate
   * 3. Cell data
   * 4. Cell activation
   * 5. Cell disabled state
   * 6. Other info (frozen, sorted asc/desc, filtered)
   * 7. Column span (if this cell spans more than one column)
   * 8. Column # and/or row number (whichever has changed)
   * 9. 'Press h to open help.' if helpProps was set
   * 10. What to do next
   * @param {TablePosition} currPos the current table position to describe
   * @param {TablePosition} prevPos the previous table position (if available)
   * @returns {string|null} the message or null if no message is needed
   */
  const createNarratorMessage = (
    currPos: TablePosition,
    prevPos?: TablePosition
  ): string | null => {
    if (currPos.region === TableRegions.ColumnHeaderButton) {
      return handleColumnHeaderButtonMessage(currPos, prevPos, props.disabled);
    }

    if (
      currPos.region === TableRegions.ZeroStateOverlay ||
      !columns ||
      columns.length === 0 ||
      rowCount === 0
    ) {
      return handleZeroStateMessage(currPos, prevPos);
    }

    // multiple or single radio selection && in the selection column (region in ColumnHeader | RowHeader)
    if (hasRowSelectionHeader && currPos.colIndex === 0) {
      return handleSelectionColumnMessage(currPos, prevPos);
    }

    let sMessage = null,
      sText;
    const aMessageParts = [];
    const colChanged: boolean = currPos.colIndex !== prevPos?.colIndex;
    const rowChanged: boolean =
      currPos.rowIndex !== prevPos?.rowIndex || (hasRowSelectionHeader && prevPos?.colIndex === 0);
    const isNonEmptyDataRow: boolean =
      currPos.region === TableRegions.Data && currPos.rowIndex > -1 && currPos.rowIndex < rowCount;
    const column = columns[currPos.colIndex];
    const rowKey = getRowKey(currPos.rowIndex);
    const disabledRow = disabled || (currPos.rowIndex > -1 && !!isRowDisabled?.(rowKey));
    const readOnlyRow = readOnly || (currPos.rowIndex > -1 && !!isRowReadOnly?.(rowKey));

    // 1. Header Context changes
    if (colChanged || (rowChanged && currPos.rowIndex < 0)) {
      sText = createColumnLabelNarration(currPos);
      if (sText !== null) {
        aMessageParts.push(sText);

        if (currPos.rowIndex < 0) {
          aMessageParts.push(`${localize('roleHeader.msg')},`);
        }
      }
    }
    if (
      rowChanged &&
      frozenColumnCount !== undefined &&
      currPos.colIndex > frozenColumnCount &&
      isNonEmptyDataRow
    ) {
      // announce any frozen column row headers
      sText = createFrozenColumnRowHeaderNarration(currPos);
      if (sText !== null) {
        aMessageParts.push(`${sText},`);
      }
    }

    // 2. Cell data
    if (isNonEmptyDataRow) {
      const cellText = createCellNarration(currPos);
      if (props.onSayCell) {
        sText = props.onSayCell({
          current: currPos,
          previous: prevPos,
          default: cellText,
        });
      } else {
        sText = cellText;
      }
      aMessageParts.push(`${sText},`);
    }

    // 3. Selection state
    if (rowChanged) {
      sText = createSelectionStateNarration(currPos);
      if (sText !== null) {
        // only add message part if it was non-null/trivial
        aMessageParts.push(`${sText},`);
      }
    }

    // 4. Cell activation
    if (!disabledRow && !readOnlyRow && isNonEmptyDataRow && props.actionNarration) {
      aMessageParts.push(`${props.actionNarration},`);
    }

    // 5. Cell or row disabled state
    if (disabledRow) {
      aMessageParts.push(`${localize('disabled.msg')},`);
    }

    // 6. Status elements - frozen, sorted asc/desc, filtered
    // announce column specific state only on column change
    if (colChanged) {
      sText = createCellStatusNarration(currPos);
      // only add the text if this cell has a non-trivial status narration
      if (sText !== null) {
        aMessageParts.push(sText);
      }
    }

    // 7. Column span
    sText = createColumnSpanNarration(currPos, columnSpan);
    if (sText !== null) {
      aMessageParts.push(sText);
    }

    // 8. Column # and/or row number (whichever has changed)
    sText = createPositionChangeNarration(currPos, prevPos);
    if (sText !== null) {
      aMessageParts.push(sText);
    }

    // 9. Help element
    if (currPos.rowIndex < 0) {
      if (column?.helpProps) {
        aMessageParts.push(localize('help.msg'));
      }
    }

    // 10. What to do next
    if (
      !disabled &&
      currPos.region === TableRegions.ColumnHeader &&
      column?.sortable &&
      column?.sortDirection !== 'ascending' &&
      column?.sortDirection !== 'descending'
    ) {
      aMessageParts.push(`${localize('spaceBarSort.msg')}`);
    }

    // build a complete message if any of the above resulted in narration text
    if (aMessageParts.length > 0) {
      // join everything together with spaces (an empty array returns an empty string)
      sMessage = aMessageParts.join(' ');
    }

    return sMessage?.replace(/,$/g, '.') ?? null;
  };

  return {
    createTableNarration: () => {
      return createTableNarratorMessage();
    },
    createNarration: (currPos: TablePosition, prevPos?: TablePosition) => {
      return createNarratorMessage(currPos, prevPos);
    },
    getColumnWidthStartNarration: () => {
      return localize('columnWidthResizeStart.msg');
    },
    getColumnWidthChangeNarration: (width: string) => {
      return localize('columnWidthResizeChange.msg', { width });
    },
  };
};
